import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  ImpactReportService,
  type ImpactReportLevel,
  type LabImpactReportRow,
} from "../../services/impactReportService";
import { Card } from "../ui/Card";
import { Table } from "../ui/Table";

interface EstimateImpactReportProps {
  labType?: "wet" | "dry";
  labName: string;
  labId: string;
}

const dryLabTableColumns = [
  /**
   * Cost ($AUD)
   * Energy (kWh)
   * Emissions (kgCO2e)
   * Waste (kg)
   * Data (TB)
   */
  { key: "cost", label: "Cost ($AUD)" },
  { key: "energy", label: "Energy (kWh)" },
  { key: "emissions", label: "Emissions (kgCO2e)" },
  { key: "waste", label: "Waste (kg)" },
  { key: "data", label: "Data (TB)" },
];

const wetLabTableColumns = [
  /**
   * Cost ($AUD)
   * Energy (kWh)
   * Emissions (kgCO2e)
   * Waste (kg)
   */
  { key: "cost", label: "Cost ($AUD)" },
  { key: "energy", label: "Energy (kWh)" },
  { key: "emissions", label: "Emissions (kgCO2e)" },
  { key: "waste", label: "Waste (kg)" },
];

/**
 * Solvent volume comes from Green Chemistry, which is Silver wet only, so a
 * Bronze lab would just see an empty column for something it cannot produce.
 */
const solventVolumeColumn = { key: "solvent", label: "Solvent (L)" };

const buildTableColumns = (labType: "wet" | "dry", level: ImpactReportLevel) => {
  if (labType !== "wet") {
    return dryLabTableColumns;
  }
  return level === "silver"
    ? [...wetLabTableColumns, solventVolumeColumn]
    : wetLabTableColumns;
};

const formatNumber = (value: number) =>
  value.toLocaleString(undefined, { maximumFractionDigits: 2 });

const formatNullableNumber = (value: number | null) =>
  typeof value === "number" ? formatNumber(value) : "-";

const slugify = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

export const EstimateImpactReport = ({
  labType,
  labName,
  labId,
}: EstimateImpactReportProps) => {
  const [level, setLevel] = useState<ImpactReportLevel>("bronze");

  const { data, isLoading, isError, error } = useQuery<LabImpactReportRow>({
    queryKey: ["lab-impact-report", labId, level],
    queryFn: () => ImpactReportService.getLabImpactReport(labId, level),
    enabled: Boolean(labId),
  });

  const handleExportCsv = async () => {
    if (!data) {
      return;
    }

    const reportingYear = data.reporting_year ?? new Date().getFullYear();

    await ImpactReportService.downloadLabImpactReportCsv(labId, {
      level,
      reporting_year: data.reporting_year ?? undefined,
      filename: `impact-report-${slugify(labName)}-${level}-${reportingYear}.csv`,
    });
  };

  const hasData = data?.report_state === "available";
  const tableData = hasData
    ? [
        {
          id: data.lab_id,
          cost: formatNumber(data.metrics.cost_aud),
          energy: formatNumber(data.metrics.energy_kwh),
          emissions: formatNumber(data.metrics.emissions_kgco2e),
          waste: formatNumber(data.metrics.waste_kg),
          solvent: formatNumber(data.metrics.solvent_volume_l),
          data: formatNullableNumber(data.metrics.data_tb),
        },
      ]
    : [];

  const fallbackLabType = labType ?? "wet";
  const reportLabType = (data?.lab_type as "wet" | "dry" | undefined) ?? fallbackLabType;
  const tableColumns = buildTableColumns(reportLabType, level);
  const reportYear = data?.reporting_year ?? new Date().getFullYear();

  return (
    <Card className="border-base-300 bg-base-100 shadow-sm">
      <div className="card-body p-4 sm:p-5">
        <div className="card-title mb-2 flex-wrap items-center justify-between gap-2">
          <h3 className="text-lg">
            {labName}'s Estimated Impact Report ({reportYear})
          </h3>
          <label className="form-control w-full sm:w-36">
            <div className="label py-1">
              <span className="label-text text-xs">Level</span>
            </div>
            <select
              className="select select-bordered select-sm"
              value={level}
              aria-label="Certification level"
              onChange={(event) => {
                setLevel(event.target.value as ImpactReportLevel);
              }}
            >
              <option value="bronze">Bronze</option>
              <option value="silver">Silver</option>
            </select>
          </label>
        </div>

        {isLoading && (
          <div className="py-8 text-center text-base-content/70 bg-base-100 rounded-lg">
            <span className="loading loading-spinner loading-sm" />
            <p className="text-sm sm:text-base mt-2">Loading impact report…</p>
          </div>
        )}

        {!isLoading && isError && (
          <div className="alert alert-error">
            <span>
              {error instanceof Error
                ? error.message
                : "Failed to load impact report"}
            </span>
          </div>
        )}

        {!isLoading && !isError && hasData ? (
          <>
            <Table columns={tableColumns} data={tableData} hover={false} />
            <div className="card-actions mt-4 justify-end">
              <button className="btn btn-secondary btn-sm" onClick={handleExportCsv}>
                Export CSV
              </button>
            </div>
          </>
        ) : null}

        {!isLoading && !isError && !hasData ? (
          <div className="py-4 text-center text-base-content/70 bg-base-100 rounded-lg">
            <p className="text-sm sm:text-base font-medium mb-1">
              {data?.report_state === "unsupported"
                ? "Impact reports are not available for this lab yet"
                : `No ${reportLabType} lab data yet`}
            </p>
            <p className="text-xs sm:text-sm">
              {data?.missing_reasons?.[0]
                ? data.missing_reasons[0]
                : `Complete your lab's ${reportLabType} lab certification to generate your impact report.`}
            </p>
            <p className="text-xs mt-2 text-base-content/40">
              CSV export will be available once data is generated.
            </p>
          </div>
        ) : null}
      </div>
    </Card>
  );
};
